import Interceptor from "./Interceptor";

export const createNewSite = async (payload) => {
  try {
    const response = await Interceptor.post("/sites", payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const allCreatedSites = async ({
  environment = "",
  status = "",
  search = "",
  page = 1,
  limit = 10,
  includeDeleted = false,
} = {}) => {
  try {
    const params = {
      ...(environment && { environment }), // include only if truthy
      ...(status && { status }), // include only if truthy
      ...(search && { search }),
      page,
      limit,
      includeDeleted,
    };

    const response = await Interceptor.get("/sites", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const siteById = async (siteId) => {
  try {
    const response = await Interceptor.get(`/sites/${siteId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateSite = async (siteId, payload) => {
  try {
    const response = await Interceptor.put(`/sites/${siteId}`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteSite = async (siteId) => {
  try {
    const response = await Interceptor.delete(`/sites/${siteId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const restoreSite = async (siteId) => {
  try {
    const response = await Interceptor.post(`/sites/${siteId}/restore`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const permanentlyDeleteSite = async (siteId) => {
  try {
    const response = await Interceptor.post(
      `/sites/${siteId}/permanent-delete`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const cloneSite = async (siteId, payload) => {
  try {
    const response = await Interceptor.post(`/sites/${siteId}/clone`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const migrateSite = async (siteId, payload) => {
  try {
    const response = await Interceptor.post(
      `/sites/${siteId}/migrate`,
      payload,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Add these functions to your existing services/Site.js

export const downloadCompleteSite = async (siteId) => {
  try {
    const response = await Interceptor.get(`/sites/${siteId}/download`, {
      responseType: "blob",
      timeout: 600000, // 10 minutes for large sites
    });

    // Check if the response is actually a JSON error (blob with application/json type)
    if (
      response.data instanceof Blob &&
      response.data.type === "application/json"
    ) {
      const text = await response.data.text();
      try {
        const json = JSON.parse(text);
        if (json.error) {
          throw new Error(json.error);
        }
      } catch (parseError) {
        if (parseError instanceof Error && parseError.message) {
          throw parseError;
        }
      }
    }

    // Create blob URL and trigger download
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    // Get filename from response headers
    const contentDisposition = response.headers["content-disposition"];
    let filename = `site_backup_${Date.now()}.tar.gz`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      } else {
        const filenameMatch2 = contentDisposition.match(/filename=([^;]+)/);
        if (filenameMatch2) {
          filename = filenameMatch2[1].trim();
        }
      }
    }

    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return { success: true, filename };
  } catch (error) {
    // Handle blob error responses from axios
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const json = JSON.parse(text);
        throw new Error(json.error || "Failed to download complete site");
      } catch (parseError) {
        if (parseError.message && parseError.message !== "Unexpected token") {
          throw parseError;
        }
      }
    }

    // Handle specific error cases
    if (error.response?.status === 404) {
      throw new Error("Site not found or no backup available for download");
    } else if (error.response?.status === 403) {
      throw new Error("You don't have permission to download this site");
    } else if (error.code === "ECONNABORTED") {
      throw new Error("Download timeout - site might be too large");
    }

    throw new Error(
      error.response?.data?.error ||
        error.message ||
        "Failed to download complete site",
    );
  }
};

export const migrateEnvironment = async (siteId, payload) => {
  try {
    const response = await Interceptor.post(
      `/sites/${siteId}/migrate-environment`,
      payload,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPhpMyAdminLogin = async (siteId) => {
  try {
    const response = await Interceptor.get(`/sites/${siteId}/phpmyadmin-login`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getWpAdminLogin = async (siteId) => {
  try {
    const response = await Interceptor.get(`/sites/${siteId}/wp-admin-login`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getQuickAccessLinks = async (siteId) => {
  try {
    const response = await Interceptor.get(`/sites/${siteId}/quick-access`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get comprehensive site metrics (storage, CPU, memory, performance)
 */
export const getSiteMetrics = async (siteId) => {
  try {
    const response = await Interceptor.get(
      `/site-management/${siteId}/metrics`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get container stats (basic)
 */
export const getSiteStats = async (siteId) => {
  try {
    const response = await Interceptor.get(`/site-management/${siteId}/stats`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Configure custom domain for a site
 * @param {string} siteId - Site ID
 * @param {string} customDomain - Custom domain to add
 * @returns {Promise} - Domain configuration with DNS instructions
 */
export const configureCustomDomain = async (siteId, customDomain) => {
  try {
    const response = await Interceptor.put(`/sites/${siteId}/domain`, {
      customDomain,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Remove custom domain from a site
 * @param {string} siteId - Site ID
 * @returns {Promise} - Updated site with generated domain
 */
export const removeCustomDomain = async (siteId) => {
  try {
    const response = await Interceptor.put(`/sites/${siteId}/domain`, {
      removeCustomDomain: true,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Verify custom domain DNS configuration
 * @param {string} siteId - Site ID
 * @returns {Promise} - Verification result with DNS status
 */
export const verifyCustomDomain = async (siteId) => {
  try {
    const response = await Interceptor.get(`/sites/${siteId}/verify-domain`);
    return response.data;
  } catch (error) {
    throw error;
  }
};
